import { Box, BoxProps, Stack } from '@mui/material';
import clsx from 'clsx';
import { ReactNode } from 'react';

import { Typography, TypographyProps } from '../../Typography';

import { DsFormHelperText } from '../../../components/controls/form-helper-text';
import { InfoTooltipProps } from '../../../components/tooltip/InfoTooltip';
import { tokens } from '../../../theme/generated/tokens';
import { SxProps, WithRef } from '../../../types';
import { mergeSx } from '../../../utils';

import { Label, LabelProps } from '../Label';

import { labelWithDescriptionClassNames } from './classNames';
import { LabelWithDescriptionSize } from './types';

export type LabelWithDescriptionSlotProps = {
  /**
   * Overrides forwarded to the inner Label. `fontSize`, `color` and `ref` are
   * deliberately omitted: size/color are driven by Label's own variant and
   * state, and ref belongs to Label's root Box. Pass the association target
   * (the control's id) as `htmlFor` here.
   */
  label?: Omit<Partial<TypographyProps>, 'fontSize' | 'color' | 'ref'> & {
    htmlFor?: string;
  };
  infoTooltip?: Partial<InfoTooltipProps>;
  descriptionWrapper?: Omit<Partial<TypographyProps>, 'variant'>;
  errorMessageWrapper?: Partial<BoxProps> & { testId?: string };
  bottomContentWrapper?: Partial<BoxProps>;
};

export type LabelWithDescriptionProps = {
  /** Label text. Forwarded to the inner Label; not rendered when empty. */
  label: ReactNode;
  /**
   * Description row rendered BELOW the label — outside the label element, so
   * it never activates the control or pollutes its accessible name.
   */
  description?: ReactNode;
  /** Size variant: sm (P12) or md (P14). */
  size?: LabelWithDescriptionSize;
  /** Label font weight. Precedence: explicit labelWeight -> variant -> medium default. */
  labelWeight?: LabelProps['labelWeight'];
  /** Marks the label required and renders a red asterisk next to it. */
  required?: boolean;
  /** Content for the info tooltip rendered after the label. */
  infoTooltip?: ReactNode;
  /**
   * Error state — renders the message as the last row of the block, after
   * bottom content, via DsFormHelperText. Use when this block is the field's
   * label and nothing else in the form renders the error.
   */
  error?: ReactNode;
  /**
   * Bottom content row rendered below the description — outside the label
   * element, like every slot.
   */
  bottomContent?: ReactNode;
  /** Disabled state: dims the label and description. */
  disabled?: boolean;
  /** Per-slot props for label, infoTooltip, descriptionWrapper, and bottomContentWrapper. */
  slotProps?: LabelWithDescriptionSlotProps;
  /** Additional CSS class */
  className?: string;
  /** MUI sx prop */
  sx?: SxProps;
  /** `data-testid` attribute value */
  testId?: string;
};

/**
 * Description typography — always the small regular paragraph variant, at
 * every label size (same as the original ControlLabel/ControlGroup).
 */
const DESCRIPTION_VARIANT = 'P12R' as const;

export const LabelWithDescription = ({
  ref,
  label,
  description,
  error,
  size = 'sm',
  labelWeight,
  required,
  infoTooltip,
  bottomContent,
  disabled,
  slotProps,
  className,
  sx,
  testId,
}: WithRef<HTMLDivElement, LabelWithDescriptionProps>) => {
  const hasErrorMessage = !!error && typeof error !== 'boolean';

  return (
    <Stack
      ref={ref}
      data-testid={testId}
      className={clsx(
        labelWithDescriptionClassNames.root,
        disabled && labelWithDescriptionClassNames.disabled,
        className
      )}
      sx={sx}
    >
      {!!label && (
        <Label
          label={label}
          size={size}
          labelWeight={labelWeight}
          required={required}
          infoTooltip={infoTooltip}
          disabled={disabled}
          slotProps={{ infoTooltip: slotProps?.infoTooltip }}
          {...slotProps?.label}
        />
      )}
      {!!description && (
        <Typography
          component="p"
          variant={DESCRIPTION_VARIANT}
          {...slotProps?.descriptionWrapper}
          className={clsx(
            labelWithDescriptionClassNames.description,
            slotProps?.descriptionWrapper?.className
          )}
          sx={mergeSx(
            {
              margin: 0,
              color: disabled
                ? tokens.theme.disabled.fg
                : tokens.theme.fg.structural.secondary,
            },
            slotProps?.descriptionWrapper?.sx
          )}
        >
          {description}
        </Typography>
      )}
      {!!bottomContent && (
        <Box
          {...slotProps?.bottomContentWrapper}
          className={clsx(
            labelWithDescriptionClassNames.bottomContent,
            slotProps?.bottomContentWrapper?.className
          )}
          sx={mergeSx(
            { paddingTop: '8px' },
            slotProps?.bottomContentWrapper?.sx
          )}
        >
          {bottomContent}
        </Box>
      )}
      {hasErrorMessage && (
        <Box
          data-testid={slotProps?.errorMessageWrapper?.testId}
          {...slotProps?.errorMessageWrapper}
          className={clsx(
            labelWithDescriptionClassNames.errorMessage,
            slotProps?.errorMessageWrapper?.className
          )}
          sx={mergeSx(
            { paddingTop: '4px' },
            slotProps?.errorMessageWrapper?.sx
          )}
        >
          <DsFormHelperText error>{error}</DsFormHelperText>
        </Box>
      )}
    </Stack>
  );
};

LabelWithDescription.displayName = 'LabelWithDescription';
